import { useEffect, useMemo, useState } from 'react';
import { Clock, Plus, Save, X, Trash2, Calendar, Sunrise, PartyPopper } from 'lucide-react';
import supabase from '../lib/supabase';
import HoursEditor from '../components/HoursEditor';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Select, StatusLine,
} from '../components/ui';

type PeakWindow = { day: number; from: string; to: string };

type Block = {
  id: string;
  court_id: string;
  start_at: string;
  end_at: string;
  reason: string | null;
  created_at: string;
  courts?: { id: string; name: string };
};

type Closure = {
  id: string;
  club_id: string;
  starts_at: string;
  ends_at: string;
  kind: 'holiday' | 'maintenance' | 'private_event' | 'other';
  reason: string | null;
};

type CourtLite = { id: string; name: string; is_active: boolean };

const CLOSURE_TONE: Record<Closure['kind'], 'amber' | 'blue' | 'gray' | 'lime'> = {
  holiday:       'lime',
  maintenance:   'amber',
  private_event: 'blue',
  other:         'gray',
};

const CLOSURE_LABEL: Record<Closure['kind'], string> = {
  holiday:       'Holiday',
  maintenance:   'Maintenance',
  private_event: 'Private event',
  other:         'Closed',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ClubSchedule({ clubId }: { clubId: string }) {
  const [windows, setWindows] = useState<PeakWindow[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [closures, setClosures] = useState<Closure[]>([]);
  const [courts, setCourts] = useState<CourtLite[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const [addingClosure, setAddingClosure] = useState(false);

  const load = async () => {
    const nowIso = new Date().toISOString();
    const [peakRes, courtsRes, blocksRes, closuresRes] = await Promise.all([
      supabase.from('court_peak_hours').select('windows').eq('club_id', clubId).maybeSingle(),
      supabase.from('courts').select('id, name, is_active').eq('club_id', clubId).order('name'),
      supabase
        .from('court_availability_blocks')
        .select('id, court_id, start_at, end_at, reason, created_at, courts!inner(id, name, club_id)')
        .eq('courts.club_id', clubId)
        .gte('end_at', nowIso)
        .order('start_at'),
      supabase
        .from('club_closures')
        .select('id, club_id, starts_at, ends_at, kind, reason')
        .eq('club_id', clubId)
        .gte('ends_at', nowIso)
        .order('starts_at'),
    ]);

    setWindows((peakRes.data?.windows as PeakWindow[] | undefined) ?? []);
    setCourts((courtsRes.data as CourtLite[]) ?? []);
  setBlocks((blocksRes.data ?? []) as unknown as Block[]);
    setClosures((closuresRes.data as Closure[]) ?? []);
    setLoaded(true);
  };

  useEffect(() => { load(); }, [clubId]);

  const windowsByDay = useMemo(() => {
    const by: Record<number, PeakWindow[]> = {};
    for (let d = 0; d < 7; d++) by[d] = [];
    for (const w of windows) if (by[w.day]) by[w.day].push(w);
    return by;
  }, [windows]);

  const addWindow = (day: number) => {
    setWindows((prev) => [...prev, { day, from: '17:00', to: '22:00' }]);
  };

  const updateWindow = (idx: number, patch: Partial<PeakWindow>) => {
    setWindows((prev) => prev.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  };

  const removeWindow = (idx: number) => {
    setWindows((prev) => prev.filter((_, i) => i !== idx));
  };

  const savePeakHours = async () => {
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from('court_peak_hours')
      .upsert({ club_id: clubId, windows }, { onConflict: 'club_id' });
    setSaving(false);
    setMsg(error
      ? { kind: 'error', text: error.message }
      : { kind: 'success', text: 'Peak hours saved.' });
  };

  const deleteBlock = async (b: Block) => {
    if (!confirm(`Delete availability block on ${b.courts?.name}?`)) return;
    await supabase.from('court_availability_blocks').delete().eq('id', b.id);
    load();
  };

  const deleteClosure = async (c: Closure) => {
    if (!confirm(`Remove ${CLOSURE_LABEL[c.kind].toLowerCase()} on ${fmtDate(c.starts_at)}?`)) return;
    await supabase.from('club_closures').delete().eq('id', c.id);
    load();
  };

  if (!loaded) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  const globalIdx = (day: number, pos: number): number => {
    let seen = -1;
    for (let i = 0; i < windows.length; i++) {
      if (windows[i].day === day) {
        seen++;
        if (seen === pos) return i;
      }
    }
    return -1;
  };

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title="Schedule"
        subtitle="Operating hours, peak-hour pricing, and one-off availability blocks for your courts."
      />

      <Section
        title={<span className="flex items-center gap-2"><Sunrise className="w-4 h-4 text-[#2d5016]" /> Operating hours</span>}
        subtitle="Customers can only book courts while your club is open. Add one window per day, or multiple for split shifts."
      >
        <HoursEditor
          table="club_hours"
          entityCol="club_id"
          entityId={clubId}
          startCol="open_time"
          endCol="close_time"
          defaultStart="07:00"
          defaultEnd="23:00"
        />
      </Section>

      <Section
        title={<span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#2d5016]" /> Peak hours</span>}
        subtitle="During these windows each court's peak rate applies; outside them, the off-peak rate."
        actions={
          <Button size="sm" onClick={savePeakHours} loading={saving} icon={Save}>
            Save peak hours
          </Button>
        }
      >
        <div className="divide-y divide-gray-100">
          {DAY_NAMES.map((dayLabel, dayIdx) => (
            <div key={dayIdx} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <div className="w-14 text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">{dayLabel}</div>
              <div className="flex-1 flex flex-wrap gap-2">
                {windowsByDay[dayIdx].length === 0 && (
                  <span className="text-xs text-gray-400 mt-2 italic">Off-peak all day</span>
                )}
                {windowsByDay[dayIdx].map((w, pos) => {
                  const gi = globalIdx(dayIdx, pos);
                  return (
                    <div key={gi}
                      className="flex items-center gap-1 bg-[#c6f806]/20 border border-[#2d5016]/20 rounded-lg px-2 py-1"
                    >
                      <input type="time" value={w.from}
                        onChange={(e) => updateWindow(gi, { from: e.target.value })}
                        className="text-sm bg-transparent focus:outline-none w-20 font-medium text-[#1a3010]" />
                      <span className="text-xs text-[#2d5016]/70">–</span>
                      <input type="time" value={w.to}
                        onChange={(e) => updateWindow(gi, { to: e.target.value })}
                        className="text-sm bg-transparent focus:outline-none w-20 font-medium text-[#1a3010]" />
                      <button onClick={() => removeWindow(gi)}
                        className="text-[#2d5016]/60 hover:text-red-600 ml-1" aria-label="Remove window">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                <button onClick={() => addWindow(dayIdx)}
                  className="text-xs text-[#2d5016] hover:underline flex items-center gap-1 mt-1.5 font-medium">
                  <Plus className="w-3 h-3" /> Add window
                </button>
              </div>
            </div>
          ))}
        </div>

        {msg && <div className="mt-3"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}
      </Section>

      <Section
        title={<span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#2d5016]" /> Availability blocks</span>}
        subtitle="Close specific courts for maintenance, tournaments, or private events. Existing bookings during the block are not automatically cancelled."
        actions={
          <Button size="sm" icon={Plus} onClick={() => setAdding(true)} disabled={courts.length === 0}>
            Block time
          </Button>
        }
      >
        {courts.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="Add courts first"
            description="You need at least one court before you can block time. Head to the Courts page to add one."
          />
        ) : blocks.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No upcoming blocks"
            description="When you need to take a court offline, block the time here so customers can't book it."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-semibold">Court</th>
                <th className="text-left py-2 font-semibold">Start</th>
                <th className="text-left py-2 font-semibold">End</th>
                <th className="text-left py-2 font-semibold">Reason</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                  <td className="py-3 text-gray-900 font-medium">{b.courts?.name ?? '—'}</td>
                  <td className="py-3 text-gray-700">{fmt(b.start_at)}</td>
                  <td className="py-3 text-gray-700">{fmt(b.end_at)}</td>
                  <td className="py-3 text-gray-600">{b.reason || <span className="italic text-gray-400">—</span>}</td>
                  <td className="py-3 text-right">
                    <Button size="sm" variant="danger" icon={Trash2} onClick={() => deleteBlock(b)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title={<span className="flex items-center gap-2"><PartyPopper className="w-4 h-4 text-[#2d5016]" /> Holidays & closures</span>}
        subtitle="Close the whole club for public holidays, maintenance days, or private events. Existing bookings aren't cancelled automatically — notify affected customers."
        actions={
          <Button size="sm" icon={Plus} onClick={() => setAddingClosure(true)}>
            Add closure
          </Button>
        }
      >
        {closures.length === 0 ? (
          <EmptyState
            icon={PartyPopper}
            title="No upcoming closures"
            description="Add a holiday or maintenance day — bookings for that window will be blocked."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-semibold">Kind</th>
                <th className="text-left py-2 font-semibold">From</th>
                <th className="text-left py-2 font-semibold">Until</th>
                <th className="text-left py-2 font-semibold">Reason</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {closures.map((c) => (
                <tr key={c.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                  <td className="py-3">
                    <Badge tone={CLOSURE_TONE[c.kind]}>{CLOSURE_LABEL[c.kind]}</Badge>
                  </td>
                  <td className="py-3 text-gray-700">{fmt(c.starts_at)}</td>
                  <td className="py-3 text-gray-700">{fmt(c.ends_at)}</td>
                  <td className="py-3 text-gray-600">
                    {c.reason || <span className="italic text-gray-400">—</span>}
                  </td>
                  <td className="py-3 text-right">
                    <Button size="sm" variant="danger" icon={Trash2} onClick={() => deleteClosure(c)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {adding && (
        <BlockForm
          courts={courts}
          onClose={() => setAdding(false)}
          onSaved={async () => { setAdding(false); await load(); }}
        />
      )}

      {addingClosure && (
        <ClosureForm
          clubId={clubId}
          onClose={() => setAddingClosure(false)}
          onSaved={async () => { setAddingClosure(false); await load(); }}
        />
      )}
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmt(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function BlockForm({
  courts, onClose, onSaved,
}: {
  courts: CourtLite[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [courtId, setCourtId] = useState(courts[0]?.id ?? '');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const start = `${startDate}T${startTime}:00`;
    const end = `${endDate || startDate}T${endTime}:00`;
    if (new Date(end) <= new Date(start)) {
      setBusy(false);
      setErr('End must be after start.');
      return;
    }
    const { error } = await supabase.from('court_availability_blocks').insert({
      court_id: courtId,
      start_at: new Date(start).toISOString(),
      end_at: new Date(end).toISOString(),
      reason: reason || null,
    });
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal title="Block time" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Court">
          <Select value={courtId} onChange={(e) => setCourtId(e.target.value)} required>
            {courts.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.is_active ? '' : ' (hidden)'}</option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </Field>
          <Field label="Start time">
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
          </Field>
          <Field label="End date" hint="Leave blank to match start date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          <Field label="End time">
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
          </Field>
        </div>

        <Field label="Reason" hint="Shown internally only.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Maintenance, tournament, private event…" />
        </Field>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">Block time</Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Whole-club closure: holidays, maintenance days, one-offs. Defaults to
 * an all-day block in the Dubai timezone; the user can toggle a time
 * range if they only want to close for part of the day.
 */
function ClosureForm({
  clubId, onClose, onSaved,
}: { clubId: string; onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<Closure['kind']>('holiday');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const start = allDay
      ? new Date(`${startDate}T00:00:00`)
      : new Date(`${startDate}T${startTime}:00`);
    const end = allDay
      ? new Date(`${endDate || startDate}T23:59:59`)
      : new Date(`${endDate || startDate}T${endTime}:00`);
    if (end <= start) {
      setBusy(false);
      setErr('End must be after start.');
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('club_closures').insert({
      club_id:    clubId,
      starts_at:  start.toISOString(),
      ends_at:    end.toISOString(),
      kind,
      reason:     reason || null,
      created_by: user?.id ?? null,
    });
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal
      title="Add closure"
      subtitle="Block the whole club for this window. Bookings will be refused during it."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value as Closure['kind'])}>
            <option value="holiday">Holiday</option>
            <option value="maintenance">Maintenance</option>
            <option value="private_event">Private event</option>
            <option value="other">Other</option>
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="From">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </Field>
          <Field label="Until" hint="Leave blank to match start">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="w-4 h-4 accent-[#2d5016]"
          />
          All day
        </label>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start time">
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </Field>
            <Field label="End time">
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </Field>
          </div>
        )}

        <Field label="Reason" hint="Shown internally only.">
          <Input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="UAE National Day, court resurfacing, etc."
          />
        </Field>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">Save closure</Button>
        </div>
      </form>
    </Modal>
  );
}
